import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, X } from 'lucide-react';

interface SocialProofEvent {
  location: string;
  productName: string;
  productImage: string;
  timeAgo: string;
}

const socialProofEvents: SocialProofEvent[] = [
  { location: 'Orchard, Singapore', productName: 'iPhone 15 Pro Max', productImage: '/placeholder-iphone.jpg', timeAgo: '2 min ago' },
  { location: 'Tampines, Singapore', productName: 'Samsung Galaxy S24 Ultra', productImage: '/placeholder-samsung.jpg', timeAgo: '5 min ago' },
  { location: 'Jurong, Singapore', productName: 'MacBook Air M3', productImage: '/placeholder-macbook.jpg', timeAgo: '8 min ago' },
  { location: 'Woodlands, Singapore', productName: 'AirPods Pro 2', productImage: '/placeholder-airpods.jpg', timeAgo: '12 min ago' },
  { location: 'Bedok, Singapore', productName: 'Sony WH-1000XM5', productImage: '/placeholder-sony.jpg', timeAgo: '15 min ago' },
  { location: 'Bishan, Singapore', productName: 'iPad Pro 12.9"', productImage: '/placeholder-ipad.jpg', timeAgo: '18 min ago' },
  { location: 'Punggol, Singapore', productName: 'Google Pixel 8 Pro', productImage: '/placeholder-pixel.jpg', timeAgo: '22 min ago' },
  { location: 'Sengkang, Singapore', productName: 'Nintendo Switch OLED', productImage: '/placeholder-switch.jpg', timeAgo: '25 min ago' },
];

const CYCLE_INTERVAL = 30000;
const DISPLAY_DURATION = 6000;

export function SocialProofPopup() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const displayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const initialDelay = setTimeout(() => {
      showNext();
    }, 10000);

    return () => clearTimeout(initialDelay);
  }, []);

  const showNext = () => {
    setCurrentIndex((prev) => (prev + 1) % socialProofEvents.length);
    setIsVisible(true);

    if (displayTimerRef.current) clearTimeout(displayTimerRef.current);
    displayTimerRef.current = setTimeout(() => {
      setIsVisible(false);
    }, DISPLAY_DURATION);

    if (intervalRef.current) clearTimeout(intervalRef.current);
    intervalRef.current = setTimeout(() => {
      showNext();
    }, CYCLE_INTERVAL);
  };

  const handleClose = () => {
    setIsVisible(false);
    if (intervalRef.current) clearTimeout(intervalRef.current);
  };

  const event = socialProofEvents[currentIndex];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: -100, y: 20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -100, y: 20 }}
          transition={{ duration: 0.4 }}
          className="fixed bottom-20 md:bottom-4 left-4 z-40 max-w-sm"
          role="status"
          aria-live="polite"
        >
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-800 p-3 pr-8">
            <button
              onClick={handleClose}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Dismiss notification"
            >
              <X className="w-3.5 h-3.5 text-muted-foreground" />
            </button>

            <div className="flex items-center gap-3">
              <div className="relative flex-shrink-0">
                <img
                  src={event.productImage}
                  alt={event.productName}
                  className="w-12 h-12 rounded-lg object-cover bg-gray-100 dark:bg-gray-800"
                />
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
              </div>

              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">
                  Someone from <span className="font-medium text-foreground">{event.location}</span>
                </p>
                <p className="text-sm font-medium truncate">{event.productName}</p>
                <p className="text-xs text-muted-foreground">{event.timeAgo}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
